import { FunctionComponent, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { faCaretUp } from "@fortawesome/pro-solid-svg-icons";

import { Icon } from "@/components/Icon/Icon";

import { filterVariableGroups, OutreachVariableGroupEntry } from "../utils/outreachVariables";
import { InstructionMenuPopover } from "./InstructionMenuPopover";

interface InstructionVariablesMenuProps {
	groups: OutreachVariableGroupEntry[];
	/** Insert the token at the textarea's caret. */
	onInsert: (token: string) => void;
	portalContainer?: HTMLElement | null;
}

/**
 * The searchable **Variables** dropdown under the instruction textarea (PREC-3687 AC 7).
 *
 * Groups are Trigger / Event / Lead in that order, each entry showing a live example value so the
 * user can see what the token stands for. The Event group is present only while event outreach is
 * on — that is decided upstream in `getVariableGroups`, so this component just renders what it is
 * handed.
 */
export const InstructionVariablesMenu: FunctionComponent<InstructionVariablesMenuProps> = ({
	groups,
	onInsert,
	portalContainer,
}) => {
	const { t } = useTranslation();
	const ti = (key: string) => t(`list.outreach.general.instruction.${key}`);

	const triggerRef = useRef<HTMLButtonElement>(null);
	const [isOpen, setIsOpen] = useState(false);
	const [search, setSearch] = useState("");

	const close = () => {
		setIsOpen(false);
		setSearch("");
	};

	const visibleGroups = filterVariableGroups(groups, search);

	return (
		<>
			<button
				ref={triggerRef}
				className="flex h-[30px] cursor-pointer items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 text-xs font-semibold text-text duration-200 hover:border-brand-light"
				type="button"
				onClick={() => (isOpen ? close() : setIsOpen(true))}
			>
				{ti("variables")}
				<Icon className="h-3 w-3 text-text-meta" icon={faCaretUp} />
			</button>
			{isOpen && (
				<InstructionMenuPopover
					anchorEl={triggerRef.current}
					portalContainer={portalContainer}
					searchName="instruction-variables"
					searchPlaceholder={ti("searchVariables")}
					searchValue={search}
					onClose={close}
					onSearchChange={setSearch}
				>
					{visibleGroups.length === 0 ? (
						<p className="px-3 py-2 text-xs italic text-text-meta">{ti("noMatches")}</p>
					) : (
						visibleGroups.map((group) => (
							<div key={group.group}>
								<div className="px-3 pb-1 pt-2 text-[11px] font-bold uppercase tracking-wide text-text-meta">
									{ti(`groups.${group.group}`)}
								</div>
								{group.entries.map((entry) => (
									<button
										key={entry.token}
										className="flex w-full cursor-pointer flex-col items-start gap-0.5 px-3 py-1.5 text-left duration-150 hover:bg-selected-item"
										type="button"
										onClick={() => {
											onInsert(entry.token);
											close();
										}}
									>
										<span className="font-mono text-xs font-semibold text-brand-light">
											{entry.token}
										</span>
										<span className="truncate text-[11px] text-text-meta">{entry.example}</span>
									</button>
								))}
							</div>
						))
					)}
				</InstructionMenuPopover>
			)}
		</>
	);
};
